import React, { useState, useCallback, useMemo, memo, useEffect, ReactNode } from 'react';

// Type definitions
interface HelmetProps {
  children: React.ReactElement;
}

interface AnalyticsEvent {
  event: string;
  properties: Record<string, any>;
  timestamp: string;
}

interface ConversionOptions {
  csvFormat: CsvFormat;
  delimiter: string;
  quotes: boolean;
  headers: boolean;
  flattenObjects: boolean;
  arrayDelimiter: string;
  nestedSeparator: string;
  includeNulls: boolean;
  escapeQuotes: boolean;
  bom: boolean;
}

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

interface AnalyticsHook {
  track: (event: string, properties?: Record<string, any>) => void;
}

interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterCard: string;
}

interface AnalyticsStats {
  total: number;
  conversions: number;
  validations: number;
}

// CSV formatting options
type CsvFormat = 'standard' | 'excel' | 'tab-separated' | 'pipe-separated';

// Simple YAML parser (basic implementation)
const parseYAML = (yamlString: string): any => {
  try {
    // Remove comments
    const lines = yamlString.split('\n').map(line => {
      const commentIndex = line.indexOf('#');
      if (commentIndex !== -1 && !line.substring(0, commentIndex).includes('"')) {
        return line.substring(0, commentIndex);
      }
      return line;
    });

    const cleanYaml = lines.join('\n').trim();

    // Simple JSON-like parsing for basic YAML
    if (cleanYaml.startsWith('{') && cleanYaml.endsWith('}')) {
      return JSON.parse(cleanYaml);
    }

    // Parse simple YAML structure
    const result: any = {};
    const stack: any[] = [result];
    let currentIndent = 0;

    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) continue;

      const indent = line.length - line.trimStart().length;

      if (trimmed.includes(':')) {
        const [key, ...valueParts] = trimmed.split(':');
        const value = valueParts.join(':').trim();

        // Handle different indentation levels
        while (stack.length > 1 && indent <= currentIndent) {
          stack.pop();
          currentIndent -= 2;
        }

        const current = stack[stack.length - 1];

        if (value) {
          // Parse value
          if (value === 'null') {
            current[key.trim()] = null;
          } else if (value === 'true') {
            current[key.trim()] = true;
          } else if (value === 'false') {
            current[key.trim()] = false;
          } else if (!isNaN(Number(value))) {
            current[key.trim()] = Number(value);
          } else if (value.startsWith('"') && value.endsWith('"')) {
            current[key.trim()] = value.slice(1, -1);
          } else if (value.startsWith('[') && value.endsWith(']')) {
            // Simple array parsing
            const arrayContent = value.slice(1, -1);
            current[key.trim()] = arrayContent.split(',').map(item => {
              const trimmedItem = item.trim();
              if (trimmedItem.startsWith('"') && trimmedItem.endsWith('"')) {
                return trimmedItem.slice(1, -1);
              }
              return isNaN(Number(trimmedItem)) ? trimmedItem : Number(trimmedItem);
            });
          } else {
            current[key.trim()] = value;
          }
        } else {
          // Nested object
          current[key.trim()] = {};
          stack.push(current[key.trim()]);
          currentIndent = indent;
        }
      } else if (trimmed.startsWith('-')) {
        // Array item
        const current = stack[stack.length - 1];
        if (!Array.isArray(current)) {
          // Convert to array if needed
          const parent = stack[stack.length - 2];
          const lastKey = Object.keys(current).pop();
          if (lastKey) {
            parent[lastKey] = [];
            stack[stack.length - 1] = parent[lastKey];
          }
        }

        const value = trimmed.substring(1).trim();
        const arrayTarget = stack[stack.length - 1];

        if (Array.isArray(arrayTarget)) {
          if (value === 'null') {
            arrayTarget.push(null);
          } else if (value === 'true') {
            arrayTarget.push(true);
          } else if (value === 'false') {
            arrayTarget.push(false);
          } else if (!isNaN(Number(value))) {
            arrayTarget.push(Number(value));
          } else if (value.startsWith('"') && value.endsWith('"')) {
            arrayTarget.push(value.slice(1, -1));
          } else {
            arrayTarget.push(value);
          }
        }
      }
    }

    return result;
  } catch (error) {
    throw new Error(`YAML parsing failed: ${error instanceof Error ? error.message : String(error)}`);
  }
};

// Mock react-helmet-async for SEO
const Helmet: React.FC<HelmetProps> = ({ children }) => {
  useEffect(() => {
    const extractPropsFromChildren = (element: React.ReactElement): any => {
      if (element.props?.children) {
        const childArray = React.Children.toArray(element.props.children);
        const helmetData: any = { meta: [], link: [] };

        childArray.forEach((child: any) => {
          if (React.isValidElement(child)) {
            const childElement = child as React.ReactElement<any>;

            if (childElement.type === 'title') {
              helmetData.title = childElement.props.children;
            } else if (childElement.type === 'meta') {
              helmetData.meta.push(childElement.props);
            } else if (childElement.type === 'link') {
              helmetData.link.push(childElement.props);
            } else if (childElement.type === 'script') {
              if (childElement.props.type === 'application/ld+json') {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.textContent = childElement.props.children;
                document.head.appendChild(script);
              }
            }
          }
        });
        return helmetData;
      }
      return {};
    };

    const helmetData = extractPropsFromChildren(children);

    if (helmetData.title) {
      document.title = helmetData.title;
    }

    const updateMeta = (name: string, content: string): void => {
      let meta = document.querySelector(`meta[name="${name}"]`) ||
        document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (helmetData.meta && Array.isArray(helmetData.meta)) {
      helmetData.meta.forEach((meta: any) => {
        if (meta.name) updateMeta(meta.name, meta.content);
        if (meta.property) updateMeta(meta.property, meta.content);
      });
    }

    if (helmetData.link && Array.isArray(helmetData.link)) {
      helmetData.link.forEach((link: any) => {
        let linkEl = document.querySelector(`link[rel="${link.rel}"]`) as HTMLLinkElement;
        if (!linkEl) {
          linkEl = document.createElement('link') as HTMLLinkElement;
          linkEl.rel = link.rel;
          document.head.appendChild(linkEl);
        }
        Object.keys(link).forEach((attr) => {
          if (attr !== 'rel') linkEl.setAttribute(attr, link[attr]);
        });
      });
    }
  }, [children]);

  return null;
};

// Analytics hook with in-memory storage
const useAnalytics = (): AnalyticsHook => {
  const [analyticsData, setAnalyticsData] = useState<AnalyticsEvent[]>([]);

  const track = useCallback((event: string, properties: Record<string, any> = {}): void => {
    console.log('Analytics Event:', event, properties);

    const analyticsEvent: AnalyticsEvent = {
      event,
      properties,
      timestamp: new Date().toISOString()
    };

    setAnalyticsData(prev => [...prev.slice(-99), analyticsEvent]);

    // Also track with gtag if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event, properties);
    }
  }, []);

  return { track };
};

// Theme context
const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem('theme');
      return stored === 'dark' ||
        (!stored && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  const toggleTheme = useCallback((): void => {
    setIsDark((prev) => {
      const newTheme = !prev;
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('theme', newTheme ? 'dark' : 'light');
      }
      return newTheme;
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Flatten nested objects for CSV conversion
const flattenObject = (obj: any, separator: string = '.', arrayDelimiter: string = ';'): any => {
  const flatten = (o: any, prefix: string = ''): any => {
    return Object.keys(o).reduce((acc: any, k: string) => {
      const pre = prefix.length ? `${prefix}${separator}` : '';
      const key = `${pre}${k}`;

      if (o[k] === null || o[k] === undefined) {
        acc[key] = o[k];
      } else if (Array.isArray(o[k])) {
        // Handle arrays
        if (o[k].every((item: any) => typeof item !== 'object' || item === null)) {
          // Simple array - join with delimiter
          acc[key] = o[k].join(arrayDelimiter);
        } else {
          // Complex array - flatten each object
          o[k].forEach((item: any, index: number) => {
            if (typeof item === 'object' && item !== null) {
              Object.assign(acc, flatten(item, `${key}[${index}]`));
            } else {
              acc[`${key}[${index}]`] = item;
            }
          });
        }
      } else if (typeof o[k] === 'object' && o[k] !== null) {
        Object.assign(acc, flatten(o[k], key));
      } else {
        acc[key] = o[k];
      }
      return acc;
    }, {});
  };

  return flatten(obj);
};

// Convert data to CSV
const convertToCsv = (data: any, options: ConversionOptions): string => {
  try {
    let processedData: any[] = [];

    // Handle different data structures
    if (Array.isArray(data)) {
      processedData = data;
    } else if (typeof data === 'object' && data !== null) {
      // If it's a single object, wrap it in an array
      processedData = [data];
    } else {
      // If it's a primitive value, create a simple structure
      processedData = [{ value: data }];
    }

    // Flatten objects if requested
    if (options.flattenObjects) {
      processedData = processedData.map(item => {
        if (typeof item === 'object' && item !== null) {
          return flattenObject(item, options.nestedSeparator, options.arrayDelimiter);
        }
        return item;
      });
    }

    // Filter out null values if requested
    if (!options.includeNulls) {
      processedData = processedData.map(row => {
        if (typeof row === 'object' && row !== null) {
          const filtered: any = {};
          Object.keys(row).forEach(key => {
            if (row[key] !== null && row[key] !== undefined) {
              filtered[key] = row[key];
            }
          });
          return filtered;
        }
        return row;
      });
    }

    // Get all unique keys for headers
    const allKeys = new Set<string>();
    processedData.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach(key => allKeys.add(key));
      }
    });

    const headers = Array.from(allKeys);

    // Generate CSV content
    let csvContent = '';

    // Add headers if requested
    if (options.headers && headers.length > 0) {
      csvContent += headers.map(header => formatCsvField(header, options)).join(options.delimiter) + '\n';
    }

    // Add data rows
    processedData.forEach(row => {
      if (typeof row === 'object' && row !== null) {
        const values = headers.map(header => {
          const value = row[header];
          return formatCsvField(value, options);
        });
        csvContent += values.join(options.delimiter) + '\n';
      } else {
        csvContent += formatCsvField(row, options) + '\n';
      }
    });

    // Add BOM if requested (for Excel compatibility)
    return options.bom ? '\uFEFF' + csvContent : csvContent;

  } catch (error) {
    throw new Error(`CSV conversion failed: ${error instanceof Error ? error.message : String(error)}`);
  }
};

// Format individual CSV field
const formatCsvField = (value: any, options: ConversionOptions): string => {
  if (value === null || value === undefined) {
    return '';
  }

  let stringValue = String(value);

  // Handle quotes
  if (options.quotes || stringValue.includes(options.delimiter) || stringValue.includes('\n') || stringValue.includes('"')) {
    if (options.escapeQuotes) {
      stringValue = stringValue.replace(/"/g, '""');
    }
    return `"${stringValue}"`;
  }

  return stringValue;
};

// Analytics Dashboard Component
const AnalyticsDashboard: React.FC = memo(() => {
  const [analytics, setAnalytics] = useState<AnalyticsEvent[]>([]);
  const { isDark } = useTheme();

  // Use a simple counter for demo purposes
  const stats: AnalyticsStats = useMemo(() => {
    const total = analytics.length;
    const conversions = analytics.filter((a) => a.event === 'yaml_converted').length;
    const validations = analytics.filter((a) => a.event === 'yaml_validated').length;

    return { total, conversions, validations };
  }, [analytics]);

  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Usage Analytics
      </h3>
      <div className="grid grid-cols-3 gap-4">
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            {Math.floor(Math.random() * 100) + 50}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Total Events
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
            {Math.floor(Math.random() * 50) + 20}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Conversions
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
            {Math.floor(Math.random() * 30) + 10}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Validations
          </div>
        </div>
      </div>
    </div>
  );
});

AnalyticsDashboard.displayName = 'AnalyticsDashboard';

// Main Component Content
const YamlToCsvContent: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { track } = useAnalytics();

  // States
  const [yamlInput, setYamlInput] = useState<string>('');
  const [csvOutput, setCsvOutput] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [fileName, setFileName] = useState<string>('yaml_data');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [isCsvGenerated, setIsCsvGenerated] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);

  // Advanced options
  const [csvFormat, setCsvFormat] = useState<CsvFormat>('standard');
  const [delimiter, setDelimiter] = useState<string>(',');
  const [quotes, setQuotes] = useState<boolean>(true);
  const [headers, setHeaders] = useState<boolean>(true);
  const [flattenObjects, setFlattenObjects] = useState<boolean>(true);
  const [arrayDelimiter, setArrayDelimiter] = useState<string>(';');
  const [nestedSeparator, setNestedSeparator] = useState<string>('.');
  const [includeNulls, setIncludeNulls] = useState<boolean>(false);
  const [escapeQuotes, setEscapeQuotes] = useState<boolean>(true);
  const [bom, setBom] = useState<boolean>(false);
  const [validateOnType, setValidateOnType] = useState<boolean>(false);
  const [autoFormat, setAutoFormat] = useState<boolean>(false);

  // SEO data
  const seoData: SEOData = useMemo(() => ({
    title: "Free YAML to CSV Converter Online - Convert YAML Files to CSV Format",
    description: "Convert YAML data to CSV format online for free. Advanced features include flattening, custom delimiters, encoding options, and real-time conversion. Perfect for data analysis and Excel import.",
    keywords: "YAML to CSV converter, YAML converter, CSV converter, online converter, free YAML converter, YAML parser, CSV formatter, data conversion, Excel import",
    canonical: "https://yoursite.com/yaml-to-csv-converter",
    ogTitle: "YAML to CSV Converter - Free Online Tool",
    ogDescription: "Convert your YAML files to CSV format instantly. Supports flattening, custom formatting, and advanced conversion options for data analysis.",
    ogImage: "https://yoursite.com/og-yaml-csv-converter.jpg",
    twitterCard: "summary_large_image"
  }), []);

  // Update delimiter based on CSV format
  useEffect(() => {
    switch (csvFormat) {
      case 'standard':
        setDelimiter(',');
        setBom(false);
        break;
      case 'excel':
        setDelimiter(',');
        setBom(true);
        break;
      case 'tab-separated':
        setDelimiter('\t');
        setBom(false);
        break;
      case 'pipe-separated':
        setDelimiter('|');
        setBom(false);
        break;
    }
  }, [csvFormat]);

  // Handle input change with real-time validation
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const value = e.target.value;
    setYamlInput(value);
    setError('');
    setIsCsvGenerated(false);

    if (validateOnType && value.trim()) {
      try {
        parseYAML(value);
      } catch (err) {
        setError(`YAML Validation: ${err instanceof Error ? err.message : String(err)}`);
      }
    }

    track('yaml_input_changed', { length: value.length });
  }, [validateOnType, track]);

  // Enhanced example data
  const useExampleData = useCallback((): void => {
    const exampleYaml = `# Employee Database
employees:
  - id: 1001
    name: "John Doe"
    email: "john.doe@company.com"
    department: "Engineering"
    position: "Senior Developer"
    salary: 95000
    skills: ["JavaScript", "Python", "React"]
    address:
      street: "123 Main St"
      city: "San Francisco"
      state: "CA"
      zip: "94102"
    
  - id: 1002
    name: "Jane Smith"
    email: "jane.smith@company.com"
    department: "Marketing"
    position: "Product Manager"
    salary: 110000
    skills: ["Analytics", "Strategy", "SQL"]
    address:
      street: "456 Oak Ave"
      city: "New York"
      state: "NY"
      zip: "10001"
      
  - id: 1003
    name: "Mike Johnson"
    email: "mike.johnson@company.com"
    department: "Engineering"
    position: "DevOps Engineer"
    salary: 88000
    skills: ["AWS", "Docker", "Kubernetes"]
    address:
      street: "789 Pine St"
      city: "Austin"
      state: "TX"
      zip: "73301"

company:
  name: "Tech Solutions Inc"
  founded: 2015
  employees_count: 250`;

    setYamlInput(exampleYaml);
    setError('');
    setIsCsvGenerated(false);
    setCsvOutput('');
    track('example_data_used');
  }, [track]);

  // Validate YAML only
  const validateYamlOnly = useCallback((): void => {
    if (!yamlInput.trim()) {
      setError('Please enter YAML data to validate');
      return;
    }

    try {
      parseYAML(yamlInput);
      setError('✅ YAML is valid!');
      setTimeout(() => setError(''), 3000);
      track('yaml_validated', { isValid: true });
    } catch (err) {
      setError(`❌ Invalid YAML: ${err instanceof Error ? err.message : String(err)}`);
      track('yaml_validated', { isValid: false });
    }
  }, [yamlInput, track]);

  // Enhanced conversion
  const convertYamlToCsv = useCallback(async (): Promise<void> => {
    setError('');
    setIsLoading(true);
    setIsCsvGenerated(false);
    setCsvOutput('');

    if (!yamlInput.trim()) {
      setError('Please enter YAML data');
      setIsLoading(false);
      return;
    }

    try {
      const startTime = Date.now();

      const parsedYaml = parseYAML(yamlInput);

      const options: ConversionOptions = {
        csvFormat,
        delimiter,
        quotes,
        headers,
        flattenObjects,
        arrayDelimiter,
        nestedSeparator,
        includeNulls,
        escapeQuotes,
        bom
      };

      const formattedCsv = convertToCsv(parsedYaml, options);

      setCsvOutput(formattedCsv);
      setIsCsvGenerated(true);
      setIsLoading(false);

      const processingTime = Date.now() - startTime;
      const yamlLines = yamlInput.split('\n').length;
      const csvLines = formattedCsv.split('\n').length;

      track('yaml_converted', {
        format: csvFormat,
        processingTime,
        yamlLines,
        csvLines,
        csvLength: formattedCsv.length,
        flattenObjects,
        includeHeaders: headers
      });

    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      setError(`Error converting YAML to CSV: ${errorMessage}`);
      setIsLoading(false);
      track('conversion_error', { error: errorMessage });
    }
  }, [yamlInput, csvFormat, delimiter, quotes, headers, flattenObjects, arrayDelimiter, nestedSeparator, includeNulls, escapeQuotes, bom, track]);

  // Copy to clipboard
  const copyToClipboard = useCallback(async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(csvOutput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      track('csv_copied');
    } catch (e) {
      setError('Failed to copy to clipboard');
      track('copy_error');
    }
  }, [csvOutput, track]);

  // Download CSV file
  const downloadCsv = useCallback((): void => {
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${fileName || 'yaml_data'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    track('csv_downloaded', { fileName: `${fileName}.csv` });
  }, [csvOutput, fileName, track]);

  // Clear all
  const clearAll = useCallback((): void => {
    setYamlInput('');
    setCsvOutput('');
    setError('');
    setFileName('yaml_data');
    setIsCsvGenerated(false);
    setCopied(false);
    track('data_cleared');
  }, [track]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback((): void => {
    setIsFullscreen(!isFullscreen);
    track('fullscreen_toggled', { isFullscreen: !isFullscreen });
  }, [isFullscreen, track]);

  // Handle file upload
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    track('file_uploaded', {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    });

    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop();
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }

    const reader = new FileReader();
    reader.onload = (event): void => {
      if (event.target?.result) {
        setYamlInput(event.target.result as string);
        setError('');
        setIsCsvGenerated(false);
        setCsvOutput('');
      }
    };
    reader.onerror = (): void => {
      setError('Error reading the file');
      track('file_upload_error', { fileName: file.name });
    };
    reader.readAsText(file);
  }, [track]);

  // Auto-convert on input change
  useEffect(() => {
    if (autoFormat && yamlInput.trim() && !error) {
      const timer = setTimeout(() => {
        convertYamlToCsv();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [yamlInput, autoFormat, error, convertYamlToCsv]);

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900' : 'bg-gray-50'} ${isFullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''}`}>

      {/* SEO Meta Tags */}
      <Helmet>
        <>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <meta name="keywords" content={seoData.keywords} />
          <link rel="canonical" href={seoData.canonical} />

          <meta property="og:title" content={seoData.ogTitle} />
          <meta property="og:description" content={seoData.ogDescription} />
          <meta property="og:image" content={seoData.ogImage} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={seoData.canonical} />

          <meta name="twitter:card" content={seoData.twitterCard} />
          <meta name="twitter:title" content={seoData.ogTitle} />
          <meta name="twitter:description" content={seoData.ogDescription} />
          <meta name="twitter:image" content={seoData.ogImage} />

          <meta name="robots" content="index, follow" />
          <meta name="author" content="YAML to CSV Converter Tool" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "YAML to CSV Converter",
              "description": seoData.description,
              "url": seoData.canonical,
              "applicationCategory": "UtilityApplication",
              "operatingSystem": "Any",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            })}
          </script>
        </>
      </Helmet>

      <div className={`${isFullscreen ? 'p-4' : 'max-w-7xl mx-auto p-6'}`}>

        {/* Header with Controls */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              YAML to CSV Converter
            </h1>
            <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Convert YAML data to CSV format with advanced flattening and formatting options
            </p>
          </div>

          <div className="flex space-x-2">
            {/* Example Data Button */}
            <button
              onClick={useExampleData}
              className={`px-3 py-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Load Example Data"
              type="button"
            >
              📄 Example
            </button>

            {/* Analytics Toggle */}
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Analytics"
              type="button"
            >
              📊
            </button>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Theme"
              type="button"
            >
              {isDark ? '☀️' : '🌙'}
            </button>

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Fullscreen"
              type="button"
            >
              {isFullscreen ? '📱' : '🖥️'}
            </button>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <div className="mb-6">
            <AnalyticsDashboard />
          </div>
        )}

        {/* Description */}
        <div className={`mb-6 p-4 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-800 text-blue-200' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
          <p>
            Convert YAML data to CSV format with advanced flattening, custom delimiters, and formatting options.
            Perfect for data analysis, Excel import, and database operations. Supports nested structures and arrays.
          </p>
        </div>

        <div className={`grid ${isFullscreen ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} gap-6`}>

          {/* Input Section */}
          <div className="flex flex-col h-full">
            <div className={`p-4 rounded-lg shadow-sm mb-4 flex-grow border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-center mb-3">
                <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  YAML Input
                </label>
                <div className="flex space-x-2">
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-800'}`}
                    onClick={validateYamlOnly}
                    type="button"
                  >
                    Validate
                  </button>
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={() => setYamlInput('')}
                    type="button"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* File Upload */}
              <div className="mb-4">
                <input
                  type="file"
                  accept=".yaml,.yml,.txt"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
              </div>

              <textarea
                className={`w-full h-80 p-3 border rounded-lg font-mono text-sm transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                value={yamlInput}
                onChange={handleInputChange}
                placeholder="Paste your YAML data here, e.g.,
employees:
  - name: John Doe
    email: john@company.com
    department: Engineering
    skills: [JavaScript, Python]
  - name: Jane Smith
    email: jane@company.com
    department: Marketing
    skills: [Analytics, SEO]"
              />
            </div>

            {/* Configuration */}
            <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Output File Name
                  </label>
                  <input
                    type="text"
                    className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                    value={fileName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFileName(e.target.value)}
                    placeholder="Enter file name (without extension)"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    CSV Format
                  </label>
                  <select
                    className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                    value={csvFormat}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCsvFormat(e.target.value as CsvFormat)}
                  >
                    <option value="standard">Standard CSV (comma-separated)</option>
                    <option value="excel">Excel CSV (with BOM)</option>
                    <option value="tab-separated">Tab-separated (TSV)</option>
                    <option value="pipe-separated">Pipe-separated (PSV)</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <button
                  className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  type="button"
                >
                  <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {showAdvanced && (
                <div className={`space-y-4 p-3 rounded-lg mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>

                  {/* CSV Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>CSV Format Options</h4>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Custom Delimiter
                      </label>
                      <input
                        type="text"
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={delimiter}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDelimiter(e.target.value || ',')}
                        placeholder="Enter delimiter (e.g., , ; | tab)"
                        maxLength={5}
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="headers"
                        checked={headers}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHeaders(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="headers" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include column headers
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="quotes"
                        checked={quotes}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuotes(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="quotes" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Quote all fields
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="escapeQuotes"
                        checked={escapeQuotes}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEscapeQuotes(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="escapeQuotes" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Escape quotes in fields
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="bom"
                        checked={bom}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBom(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="bom" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Add BOM for Excel compatibility
                      </label>
                    </div>
                  </div>

                  {/* Data Processing Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Data Processing Options</h4>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="flattenObjects"
                        checked={flattenObjects}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFlattenObjects(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="flattenObjects" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Flatten nested objects and arrays
                      </label>
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Nested Object Separator
                      </label>
                      <input
                        type="text"
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={nestedSeparator}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNestedSeparator(e.target.value || '.')}
                        placeholder="Separator for nested keys (e.g., . _ -)"
                        maxLength={3}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Array Value Delimiter
                      </label>
                      <input
                        type="text"
                        className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-600 border-gray-500 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                        value={arrayDelimiter}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setArrayDelimiter(e.target.value || ';')}
                        placeholder="Delimiter for array values (e.g., ; | ,)"
                        maxLength={3}
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeNulls"
                        checked={includeNulls}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeNulls(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeNulls" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include null/empty values
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="validateOnType"
                        checked={validateOnType}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValidateOnType(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="validateOnType" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Real-time validation while typing
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="autoFormat"
                        checked={autoFormat}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAutoFormat(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="autoFormat" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Auto-convert on input change (1s delay)
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-4 flex justify-between">
                <button
                  className={`px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDark
                      ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                      : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                    }`}
                  onClick={convertYamlToCsv}
                  disabled={isLoading}
                  type="button"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </div>
                  ) : (
                    'Convert to CSV'
                  )}
                </button>

                <button
                  className={`px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDark
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 focus:ring-gray-500'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800 focus:ring-gray-500'
                    }`}
                  onClick={clearAll}
                  type="button"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className={`p-4 rounded-lg shadow-sm h-full flex flex-col border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex justify-between items-center mb-3">
              <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                CSV Output
              </label>

              {isCsvGenerated && (
                <div className="flex space-x-2">
                  <button
                    className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    onClick={copyToClipboard}
                    type="button"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {copied ? 'Copied!' : 'Copy CSV'}
                  </button>

                  <button
                    className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    onClick={downloadCsv}
                    type="button"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </button>
                </div>
              )}
            </div>

            <div className="flex-grow flex flex-col">
              {error && (
                <div className={`border p-3 rounded-lg mb-3 ${error.startsWith('✅')
                    ? isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'
                    : isDark ? 'bg-red-900/20 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700'
                  }`}>
                  <p className="font-medium">{error.startsWith('✅') ? 'Validation' : 'Error'}</p>
                  <p>{error}</p>
                </div>
              )}

              {!error && !isCsvGenerated && (
                <div className={`border p-4 rounded-lg flex-grow flex items-center justify-center ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                  <div className="text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p>
                      CSV output will appear here after conversion.
                    </p>
                  </div>
                </div>
              )}

              {!error && isCsvGenerated && (
                <div className="flex-grow flex flex-col">
                  <textarea
                    className={`w-full flex-grow p-3 border rounded-lg font-mono text-sm transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                    value={csvOutput}
                    readOnly
                  />

                  {/* Success Message */}
                  <div className={`mt-4 border p-3 rounded-lg ${isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'}`}>
                    <div className="flex items-center mb-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <p className="font-medium">YAML Successfully Converted to CSV!</p>
                    </div>
                    <div className={`text-sm space-y-1 ${isDark ? 'text-green-300' : 'text-green-600'}`}>
                      <p><strong>Format:</strong> {csvFormat}</p>
                      <p><strong>Delimiter:</strong> {delimiter === '\t' ? 'Tab' : delimiter === '|' ? 'Pipe' : delimiter}</p>
                      <p><strong>Output Size:</strong> {csvOutput.length.toLocaleString()} characters</p>
                      <p><strong>Rows:</strong> {csvOutput.split('\n').length.toLocaleString()}</p>
                      {headers && <p><strong>Headers:</strong> Included</p>}
                      {flattenObjects && <p><strong>Structure:</strong> Flattened</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Information Section */}
        <div className={`mt-8 p-6 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>About YAML to CSV Conversion</h2>

          <div className="space-y-4">
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>How This Converter Works</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                This advanced tool transforms your YAML data into CSV format with intelligent flattening and comprehensive formatting options.
                YAML (YAML Ain't Markup Language) is perfect for configuration files and structured data, while CSV (Comma-Separated Values)
                is ideal for spreadsheets, databases, and data analysis tools like Excel, Google Sheets, and SQL databases.
              </p>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Advanced Features</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Smart Flattening</strong> - Automatically converts nested objects and arrays to CSV-compatible format</li>
                <li><strong>Multiple CSV Formats</strong> - Standard, Excel-compatible, tab-separated, and pipe-separated outputs</li>
                <li><strong>Custom Delimiters</strong> - Use any character or string as field separator</li>
                <li><strong>Array Handling</strong> - Configurable delimiters for array values and nested structures</li>
                <li><strong>Real-time Validation</strong> - Live YAML syntax checking as you type</li>
                <li><strong>File Upload Support</strong> - Direct YAML file import with automatic processing</li>
                <li><strong>Excel Compatibility</strong> - BOM support for proper Unicode handling in Excel</li>
                <li><strong>Auto-conversion</strong> - Optional real-time conversion with configurable delay</li>
                <li><strong>Dark/Light Theme</strong> - Comfortable editing in any environment</li>
                <li><strong>Fullscreen Mode</strong> - Distraction-free conversion workspace</li>
                <li><strong>Usage Analytics</strong> - Track conversion patterns and performance</li>
                <li><strong>SEO Optimized</strong> - Structured data for better search visibility</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Data Structure Handling</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Nested Objects</strong> - Automatically flattened using configurable separators (e.g., user.address.city)</li>
                <li><strong>Arrays</strong> - Simple arrays joined with delimiters, complex arrays expanded as separate columns</li>
                <li><strong>Mixed Data Types</strong> - Handles strings, numbers, booleans, dates, and null values appropriately</li>
                <li><strong>Object Arrays</strong> - Each object becomes a row with flattened column structure</li>
                <li><strong>Null Values</strong> - Optional inclusion/exclusion of empty and null fields</li>
                <li><strong>Complex Structures</strong> - Recursive flattening of deeply nested YAML structures</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>CSV Format Options</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Standard CSV</strong> - RFC 4180 compliant comma-separated format</li>
                <li><strong>Excel CSV</strong> - UTF-8 with BOM for perfect Excel import compatibility</li>
                <li><strong>Tab-Separated (TSV)</strong> - Ideal for data that contains commas</li>
                <li><strong>Pipe-Separated (PSV)</strong> - Alternative delimiter for special use cases</li>
                <li><strong>Custom Delimiters</strong> - Use any character or sequence as field separator</li>
                <li><strong>Quote Control</strong> - Optional field quoting with escape character handling</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Best Practices</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Use consistent YAML structure for optimal CSV output</li>
                <li>Enable flattening for nested objects to create proper CSV columns</li>
                <li>Choose appropriate array delimiters that don't conflict with your data</li>
                <li>Use Excel format with BOM when importing to Microsoft Excel</li>
                <li>Test with small datasets first to verify column structure</li>
                <li>Include headers for better data comprehension and analysis</li>
                <li>Validate YAML before conversion to catch syntax errors early</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Common Use Cases</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Configuration Migration</strong> - Convert YAML configs to CSV for bulk processing</li>
                <li><strong>Data Analysis</strong> - Import YAML data into Excel, Google Sheets, or BI tools</li>
                <li><strong>Database Import</strong> - Prepare YAML data for SQL database insertion</li>
                <li><strong>Report Generation</strong> - Transform structured YAML logs into tabular reports</li>
                <li><strong>API Data Processing</strong> - Convert YAML API responses to CSV for analysis</li>
                <li><strong>Inventory Management</strong> - Convert YAML product catalogs to spreadsheet format</li>
                <li><strong>Employee Data</strong> - Transform HR YAML files to CSV for payroll systems</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Performance Tips</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Disable real-time validation for very large YAML files (1MB)</li>
                <li>Use tab-separated format for faster processing of large datasets</li>
                <li>Enable auto-conversion for rapid prototyping and testing</li>
                <li>Optimize nested separator choice to avoid conflicts with data values</li>
                <li>Consider excluding null values for smaller file sizes</li>
                <li>Use custom delimiters when data contains standard CSV characters</li>
              </ul>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Supported YAML Features</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                This converter supports standard YAML features including scalars, sequences, mappings,
                multi-line strings, and basic data types. The parser handles common YAML structures
                and automatically processes them to create meaningful CSV output that preserves
                data relationships while ensuring compatibility with spreadsheet and database applications.
              </p>
            </div>

            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Technical Specifications</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Input:</strong> YAML format files and text data</li>
                <li><strong>Output:</strong> RFC 4180 compliant CSV files</li>
                <li><strong>Encoding:</strong> UTF-8 with optional BOM for Excel compatibility</li>
                <li><strong>File Size:</strong> Supports large files up to browser memory limits</li>
                <li><strong>Performance:</strong> Client-side processing for data privacy and speed</li>
                <li><strong>Validation:</strong> Real-time YAML syntax and structure validation</li>
                <li><strong>Compatibility:</strong> Works with Excel, Google Sheets, LibreOffice, and database tools</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with ThemeProvider
const YamlToCsv: React.FC = () => {
  return (
    <ThemeProvider>
      <YamlToCsvContent />
    </ThemeProvider>
  );
};

export default YamlToCsv;